import { useState, useEffect, useRef, useCallback } from 'react';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useVoiceGuidance } from '@/hooks/useVoiceGuidance';
import { X, Play, Pause, RotateCcw, Volume2, VolumeX, Home } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import { Label } from '@/components/ui/label';
import { playMetronomeTick } from '@/utils/audioUtils';
import BreathingBar from './BreathingBar';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR, CO2_WATERMARK_LOGO } from '@/utils/exerciseBackgrounds';
import AirHungerRatingModal from './AirHungerRatingModal';
import { useLastSessionSettings } from '@/hooks/useLastSessionSettings';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import ExerciseTopLogo from './ExerciseTopLogo';
import InhalationGentleWarning from './InhalationGentleWarning';

const ButeykoModal = ({ isOpen, onClose, pattern, onSessionComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale, exhale, hold
  useWakeLock(isActive && !isPaused);
  useVoiceGuidance(phase, isActive && !isPaused);
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [sessionDuration, setSessionDuration] = useState(5); // minutes
  const [remainingTime, setRemainingTime] = useState(300); // seconds
  const [holdDuration, setHoldDuration] = useState(1); // adjustable hold time
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showAirHungerModal, setShowAirHungerModal] = useState(false);
  const [lastSessionId, setLastSessionId] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  const [ratedThisSession, setRatedThisSession] = useState(false);
  
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const soundEnabledRef = useRef(true);

  // Keep ref in sync with state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Restore last-used settings (session duration + hold time).
  useLastSessionSettings(isOpen, pattern?.id || 'buteyko', (settings) => {
    if (typeof settings.sessionDuration === 'number') {
      setSessionDuration(settings.sessionDuration);
      setRemainingTime(settings.sessionDuration * 60);
    }
    if (typeof settings.holdDuration === 'number') {
      setHoldDuration(settings.holdDuration);
    }
  });

  const timings = {
    inhale: 2,
    exhale: 3,
    hold: holdDuration
  };

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsActive(false);
      setIsPaused(false);
      setPhase('ready');
      setProgress(0);
      setCyclesCompleted(0);
      setRemainingTime(sessionDuration * 60);
    }
  }, [isOpen, sessionDuration]);

  // Update remaining time when session duration changes
  useEffect(() => {
    if (!isActive) {
      setRemainingTime(sessionDuration * 60);
    }
  }, [sessionDuration, isActive]);

  // Main breathing logic
  useEffect(() => {
    if (!isActive || isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    let phaseStartTime = Date.now();
    let currentPhase = phase === 'ready' ? 'inhale' : phase;
    if (phase === 'ready') setPhase('inhale');

    const getPhaseDuration = (p) => {
      if (p === 'inhale') return timings.inhale * 1000;
      if (p === 'exhale') return timings.exhale * 1000;
      if (p === 'hold') return timings.hold * 1000;
      return 1000;
    };

    let rafId = null;
    const tick = () => {
      const elapsed = Date.now() - phaseStartTime;
      const phaseDuration = getPhaseDuration(currentPhase);
      const prog = Math.min(elapsed / phaseDuration, 1);
      setProgress(prog);

      if (elapsed >= phaseDuration) {
        // Move to next phase
        if (currentPhase === 'inhale') {
          currentPhase = 'exhale';
          setPhase('exhale');
        } else if (currentPhase === 'exhale') {
          currentPhase = 'hold';
          setPhase('hold');
        } else if (currentPhase === 'hold') {
          currentPhase = 'inhale';
          setPhase('inhale');
          setCyclesCompleted(prev => prev + 1);
        }
        phaseStartTime = Date.now();
        setProgress(0);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isActive, isPaused, phase, timings.inhale, timings.exhale, timings.hold]);

  // Metronome - tick every second
  useEffect(() => {
    if (!isActive || isPaused) return;
    
    // Play immediately when starting
    if (soundEnabledRef.current) playMetronomeTick();
    
    const metronomeInterval = setInterval(() => {
      if (soundEnabledRef.current) playMetronomeTick();
    }, 1000);
    
    return () => clearInterval(metronomeInterval);
  }, [isActive, isPaused]);

  // Session timer
  useEffect(() => {
    if (!isActive || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      setRemainingTime(prev => {
        if (prev <= 1) {
          if (onSessionComplete && elapsedTime > 10) {
            onSessionComplete({
              patternId: pattern?.id || 'buteyko',
              patternName: pattern?.name || 'Méthode Buteyko',
              durationSeconds: elapsedTime,
              cyclesCompleted: cyclesCompleted,
              settings: { sessionDuration, holdDuration },
            });
          }
          setIsActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive, isPaused, elapsedTime, cyclesCompleted, onSessionComplete, pattern]);

  const handleStart = () => {
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
    setRatedThisSession(false);
  };

  const handlePause = async () => {
    // If we're switching from playing → paused and the session is long enough,
    // treat it as "end of session" and show the rating popup (once per session).
    const goingToPause = !isPaused;
    setIsPaused(!isPaused);

    if (goingToPause && !ratedThisSession && elapsedTime > 10) {
      setRatedThisSession(true);
      let savedSession = null;
      if (onSessionComplete) {
        savedSession = await onSessionComplete({
          patternId: pattern?.id || 'buteyko',
          patternName: pattern?.name || 'Méthode Buteyko',
          durationSeconds: elapsedTime,
          cyclesCompleted: cyclesCompleted,
          settings: { sessionDuration, holdDuration },
        });
      }
      setLastSessionId(savedSession?.id || null);
      setShowAirHungerModal(true);
    }
  };

  // Intercept modal-close (X/Home) so we can show the rating popup first.
  const handleClose = async () => {
    const wasLongSession = elapsedTime > 10;
    if (wasLongSession && !ratedThisSession) {
      let savedSession = null;
      if (onSessionComplete) {
        savedSession = await onSessionComplete({
          patternId: pattern?.id || 'buteyko',
          patternName: pattern?.name || 'Méthode Buteyko',
          durationSeconds: elapsedTime,
          cyclesCompleted: cyclesCompleted,
          settings: { sessionDuration, holdDuration },
        });
      }
      setIsActive(false);
      setIsPaused(false);
      setPhase('ready');
      setProgress(0);
      setCyclesCompleted(0);
      setRemainingTime(sessionDuration * 60);
      setElapsedTime(0);
      setLastSessionId(savedSession?.id || null);
      setPendingClose(true);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    } else {
      onClose();
    }
  };

  const handleAirHungerClose = () => {
    setShowAirHungerModal(false);
    if (pendingClose) {
      setPendingClose(false);
      onClose();
    }
  };

  const handleReset = async () => {
    const wasLongSession = elapsedTime > 10;
    let savedSession = null;
    if (onSessionComplete && wasLongSession && !ratedThisSession) {
      savedSession = await onSessionComplete({
        patternId: pattern?.id || 'buteyko',
        patternName: pattern?.name || 'Méthode Buteyko',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted
      });
    }
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setRemainingTime(sessionDuration * 60);
    setElapsedTime(0);
    if (wasLongSession && !ratedThisSession) {
      setLastSessionId(savedSession?.id || null);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    }
  };

  const applyAirHungerRecommendation = (direction) => {
    // 'up' → increase hold duration by 1s; 'down-strong' → drop by 3s (minimum 2s)
    setHoldDuration((prev) => {
      if (direction === 'up') return Math.min(prev + 1, 60);
      if (direction === 'down-strong') return Math.max(prev - 3, 2);
      return prev;
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseLabel = () => {
    if (phase === 'ready') return 'Prêt';
    if (phase === 'inhale') return 'Inspirez';
    if (phase === 'exhale') return 'Expirez';
    if (phase === 'hold') return 'Retenez';
    return '';
  };

  const getPhaseColor = () => {
    if (phase === 'inhale') return 'from-emerald-400 to-teal-500';
    if (phase === 'exhale') return 'from-sky-400 to-blue-500';
    if (phase === 'hold') return 'from-amber-400 to-orange-500';
    return 'from-slate-400 to-slate-500';
  };

  // Calculate circle scale based on phase
  const getCircleScale = () => {
    if (phase === 'inhale') return 1 + progress * 0.3;
    if (phase === 'exhale') return 1.3 - progress * 0.3;
    if (phase === 'hold') return 1;
    return 1;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className="relative w-full h-full flex flex-col"
        style={{
          backgroundImage: `url(${EXERCISE_BACKGROUNDS.buteyko})`,
          backgroundColor: EXERCISE_BG_FALLBACK_COLOR,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Soft white veil to match the cabinet homepage rendering */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[1]" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,247,252,0.55) 55%, rgba(214,235,246,0.55) 100%)' }} />
        {/* No dark overlay — the light water background is intentional for CO2 exercises */}

        {/* Floating Navigation Buttons */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={handleClose}
            className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors shadow-sm"
            title="Retour aux exercices"
          >
            <Home size={20} className="text-slate-700" />
          </button>
        </div>
        
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={handleClose}
            className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors shadow-sm"
            title="Fermer"
          >
            <X size={20} className="text-slate-700" />
          </button>
        </div>

        <ExerciseTopLogo />

        {/* Main Content */}
        <div className="relative z-10 flex-1 overflow-y-auto px-4 pt-20 sm:pt-24 pb-8 flex flex-col lg:items-center lg:justify-center lg:pb-8">
          {/* ================= DESKTOP LAYOUT (lg+) — 3-column grid, capsule centered on screen ================= */}
          <div className="hidden lg:grid w-full grid-cols-[1fr_auto_1fr] items-center gap-6">
            {/* LEFT — Timer + Settings */}
            <div className="justify-self-end w-full max-w-xs space-y-4">
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md">
                <p className="text-slate-500 text-xs mb-1">Temps restant</p>
                <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>
                  {formatTime(remainingTime)}
                </p>
              </div>
              {(!isActive || isPaused) && (
                <div className="bg-white/95 backdrop-blur-md rounded-xl px-4 py-3 shadow-md border border-slate-300">
                  <h3 className="font-semibold text-slate-700 mb-4">
                    {isPaused ? 'Pause - Réglages' : 'Réglages'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600 mb-2 block">Durée</Label>
                      <StepperInput
                        value={sessionDuration}
                        onChange={(v) => { setSessionDuration(v); if (isPaused) setRemainingTime(v * 60); }}
                        min={1} max={20} step={1} unit="min" label="durée" colorClass="cyan"
                        valueTestId="buteyko-duration-value" incTestId="buteyko-duration-inc" decTestId="buteyko-duration-dec"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 mb-2 block">Rétention</Label>
                      <StepperInput
                        value={holdDuration} onChange={(v) => setHoldDuration(v)}
                        min={1} max={15} step={1} unit="sec" label="rétention" colorClass="amber"
                        valueTestId="buteyko-hold-value" incTestId="buteyko-hold-inc" decTestId="buteyko-hold-dec"
                      />
                      <p className="text-xs text-slate-500 mt-1">Commencez à 1 seconde</p>
                    </div>
                    <div className="pt-2 border-t border-slate-200 text-xs text-slate-500">
                      <p>Inspiration : {timings.inhale}s</p>
                      <p>Expiration : {timings.exhale}s</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CENTER — Capsule (dead-center on screen thanks to symmetric 1fr columns) */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <InhalationGentleWarning visible={isActive && phase === 'inhale'} />
                <BreathingBar
                  phase={phase === 'hold' ? 'hold-out' : phase}
                  progress={progress}
                  phases={['inhale', 'exhale', 'hold-out']}
                  phaseDurations={{ inhale: timings.inhale, exhale: timings.exhale, 'hold-out': timings.hold }}
                  colors={{ inhale: '#34d399', exhale: '#22d3ee', 'hold-out': '#a855f7', ready: '#94a3b8' }}
                  lightTheme
                />
                {isActive && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 bg-white/85 backdrop-blur rounded-xl px-5 py-1.5 shadow border border-slate-300 whitespace-nowrap">
                    <span className="text-base font-medium text-slate-700">{getPhaseLabel()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT — Vertical controls column (Sound / Play / Reset + Cycles) */}
            <div className="justify-self-start flex flex-col items-center gap-4">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-14 h-14 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                data-testid="buteyko-sound-btn-desktop"
              >
                {soundEnabled ? <Volume2 className="w-6 h-6 text-slate-700" /> : <VolumeX className="w-6 h-6 text-slate-400" />}
              </button>
              <button
                onClick={isActive ? handlePause : handleStart}
                className="w-20 h-20 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                data-testid="buteyko-play-btn-desktop"
              >
                {!isActive || isPaused ? <Play className="w-9 h-9 text-slate-700 ml-1" /> : <Pause className="w-9 h-9 text-slate-700" />}
              </button>
              <button
                onClick={handleReset}
                className="w-14 h-14 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(239,68,68,0.55)' }}
                data-testid="buteyko-reset-btn-desktop"
              >
                <RotateCcw className="w-6 h-6 text-red-500" />
              </button>
              <div className="bg-white/85 backdrop-blur rounded-xl px-4 py-2 shadow-md border border-slate-300 mt-2">
                <span className="text-slate-700 font-semibold">{cyclesCompleted} cycles</span>
              </div>
            </div>
          </div>

          {/* ================= MOBILE / TABLET LAYOUT (<lg) — stacked ================= */}
          <div className="lg:hidden flex flex-col items-center my-auto gap-6">
            {/* Timer */}
            <div className="bg-white/85 backdrop-blur-md rounded-xl px-4 py-2 text-center border border-slate-300 shadow-md">
              <p className="text-slate-500 text-xs mb-0.5">Temps restant</p>
              <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>{formatTime(remainingTime)}</p>
            </div>

            {/* Capsule */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <InhalationGentleWarning visible={isActive && phase === 'inhale'} />
                <BreathingBar
                  phase={phase === 'hold' ? 'hold-out' : phase}
                  progress={progress}
                  phases={['inhale', 'exhale', 'hold-out']}
                  phaseDurations={{ inhale: timings.inhale, exhale: timings.exhale, 'hold-out': timings.hold }}
                  colors={{ inhale: '#34d399', exhale: '#22d3ee', 'hold-out': '#a855f7', ready: '#94a3b8' }}
                  lightTheme
                />
                {isActive && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 bg-white/85 backdrop-blur rounded-xl px-5 py-1.5 shadow border border-slate-300 whitespace-nowrap">
                    <span className="text-base font-medium text-slate-700">{getPhaseLabel()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Settings panel (shown when not active) */}
            {(!isActive || isPaused) && (
              <div className="w-full max-w-xs bg-white/95 backdrop-blur-md rounded-xl px-4 py-3 shadow-md border border-slate-300">
                <h3 className="font-semibold text-slate-700 mb-4">{isPaused ? 'Pause - Réglages' : 'Réglages'}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600 mb-2 block">Durée</Label>
                    <StepperInput
                      value={sessionDuration}
                      onChange={(v) => { setSessionDuration(v); if (isPaused) setRemainingTime(v * 60); }}
                      min={1} max={20} step={1} unit="min" label="durée" colorClass="cyan"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 mb-2 block">Rétention</Label>
                    <StepperInput
                      value={holdDuration} onChange={(v) => setHoldDuration(v)}
                      min={1} max={15} step={1} unit="sec" label="rétention" colorClass="amber"
                    />
                    <p className="text-xs text-slate-500 mt-1">Commencez à 1 seconde</p>
                  </div>
                </div>
              </div>
            )}

            {/* Controls row */}
            <div className="w-full flex flex-col items-center gap-3">
              <div className="flex items-center gap-4 exercise-controls-mobile-fixed">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                  data-testid="buteyko-sound-btn"
                >
                  {soundEnabled ? <Volume2 className="w-6 h-6 text-slate-700" /> : <VolumeX className="w-6 h-6 text-slate-400" />}
                </button>
                <button
                  onClick={isActive ? handlePause : handleStart}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(34,211,238,0.5)' }}
                  data-testid="buteyko-play-btn"
                >
                  {!isActive || isPaused ? <Play className="w-7 h-7 sm:w-9 sm:h-9 text-slate-700 ml-1" /> : <Pause className="w-7 h-7 sm:w-9 sm:h-9 text-slate-700" />}
                </button>
                <button
                  onClick={handleReset}
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239,68,68,0.55)' }}
                  data-testid="buteyko-reset-btn"
                >
                  <RotateCcw className="w-6 h-6 text-red-500" />
                </button>
              </div>
              <div className="bg-white/85 backdrop-blur rounded-xl px-4 py-2 shadow-md border border-slate-300">
                <span className="text-slate-700 font-semibold">{cyclesCompleted} cycles</span>
              </div>
            </div>

            {/* Instructions inline (mobile) */}
            {!isActive && (
              <div className="w-full max-w-xs space-y-3">
                <div className="bg-white/95 backdrop-blur-md rounded-xl px-4 py-3 shadow-md border border-slate-300">
                  <h3 className="font-semibold text-slate-900 mb-3">Instructions</h3>
                  <ul className="text-sm text-slate-900 space-y-2">
                    <li className="flex items-start gap-2"><span className="text-slate-900 font-bold">1.</span>Inspirez légèrement par le nez</li>
                    <li className="flex items-start gap-2"><span className="text-slate-900 font-bold">2.</span>Expirez doucement par le nez</li>
                    <li className="flex items-start gap-2"><span className="text-slate-900 font-bold">3.</span>Pincez le nez et retenez</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Instructions drawer (desktop only) */}
        <ExerciseInstructionsDrawer testIdPrefix="buteyko">
          <div className="bg-white rounded-xl px-4 py-3 shadow border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-3">Instructions</h3>
            <ul className="text-sm text-slate-900 space-y-2">
              <li className="flex items-start gap-2"><span className="text-slate-900 font-bold">1.</span>Inspirez légèrement par le nez</li>
              <li className="flex items-start gap-2"><span className="text-slate-900 font-bold">2.</span>Expirez doucement par le nez</li>
              <li className="flex items-start gap-2"><span className="text-slate-900 font-bold">3.</span>Pincez le nez et retenez</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-sky-50 to-cyan-50">
              <h3 className="font-semibold text-slate-900 text-sm">Pourquoi cet exercice ?</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Petites pauses répétées — désensibilisation au CO₂</p>
            </div>
            <div className="px-4 py-3 space-y-3">
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-cyan-700 mb-1.5">Compétences travaillées</h4>
                <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                  <li>Tolérance progressive à la soif d'air</li>
                  <li>Contrôle du rythme respiratoire (moins de respirations / minute)</li>
                  <li>Respiration nasale douce et discrète</li>
                  <li>Sensibilité fine aux signaux du corps</li>
                </ul>
              </section>
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">Objectifs visés</h4>
                <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                  <li>Faire remonter progressivement le taux de CO₂</li>
                  <li>Ralentir la fréquence respiratoire au repos</li>
                  <li>Améliorer la tolérance à l'effort et diminuer l'essoufflement</li>
                  <li>Calmer le système nerveux</li>
                </ul>
              </section>
              <section>
                <h4 className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1.5">Effets bénéfiques sur le corps</h4>
                <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                  <li><strong>Meilleure oxygénation :</strong> le CO₂ ouvre les bronches et les artères — l'oxygène diffuse mieux vers les organes et le cerveau.</li>
                  <li><strong>Économie d'énergie :</strong> ralentir la respiration au repos réduit la dépense énergétique du corps. Plus d'énergie pour la vie.</li>
                  <li><strong>Apaisement nerveux :</strong> tensions musculaires en baisse, digestion et sommeil améliorés, pensées plus calmes.</li>
                  <li><strong>Endurance :</strong> la tolérance à l'effort s'améliore, l'essoufflement diminue considérablement.</li>
                  <li><strong>Cerveau & organes :</strong> mieux oxygénés, ils fonctionnent beaucoup mieux au quotidien.</li>
                </ul>
              </section>
            </div>
          </div>
        </ExerciseInstructionsDrawer>
      </div>
      <AirHungerRatingModal
        isOpen={showAirHungerModal}
        onClose={handleAirHungerClose}
        sessionId={lastSessionId}
        onApplyRecommendation={applyAirHungerRecommendation}
      />
    </div>
  );
};

export default ButeykoModal;
